import { apiClient } from './api.service';

export type ReconciliationRunStatus =
  | 'RUNNING'
  | 'PAUSED'
  | 'CANCEL_REQUESTED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'FAILED';

export type ReconciliationResultStatus =
  | 'PASS'
  | 'MISMATCH'
  | 'HEALING'
  | 'HEALED'
  | 'FAILED'
  | 'SKIPPED';

export interface ReconciliationRunStartRequest {
  tenantId?: string;
  allTenants?: boolean;
  entities?: string[];
  dryRun?: boolean;
  maxHeal?: number;
  updatedAtLagThresholdMinutes?: number;
  forceClosedMonths?: boolean;
  healEnabled?: boolean;
}

export interface ReconciliationRunListItem {
  runId: string;
  status: ReconciliationRunStatus;
  tenantScope: string;
  tenantIds: string[];
  entities: string[];
  dryRun: boolean;
  healEnabled: boolean;
  maxHeal: number;
  updatedAtLagThresholdMinutes: number;
  forceClosedMonths: boolean;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  counts: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
}

export interface ReconciliationEntityMetrics {
  clioCount: number;
  redshiftCount: number;
  countDelta: number;
  absoluteCountDelta: number;
  diffMissingInRedshift: number;
  diffExtraInRedshift: number;
  missingInDynamo: number;
  updatedAtCompared: number;
  updatedAtMissingInClio: number;
  updatedAtMissingInRedshift: number;
  updatedAtLagOverThreshold: number;
  updatedAtLagWithinThreshold: number;
  updatedAtLagMaxMinutes: number;
  updatedAtLagP95Minutes: number;
  updatedAtLagAvgMinutes: number;
  updatedAtRedshiftAheadCount: number;
  clioPagesFetched: number;
  clioRecordsScanned: number;
  clioRecordsMatched: number;
  healRequested: number;
  healFailed: number;
  resyncRequested: boolean;
}

export interface ReconciliationRunEntityStat {
  tenantId: string;
  entityKey: string;
  datasetName: string;
  taskStatus: string;
  state: string;
  resultStatus?: ReconciliationResultStatus;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  metrics: ReconciliationEntityMetrics;
}

export interface ReconciliationRunDetail {
  runId: string;
  status: ReconciliationRunStatus;
  tenantScope: string;
  tenantIds: string[];
  entities: string[];
  dryRun: boolean;
  healEnabled: boolean;
  maxHeal: number;
  updatedAtLagThresholdMinutes: number;
  forceClosedMonths: boolean;
  createdAt: string;
  startedAt?: string;
  updatedAt: string;
  completedAt?: string;
  counts: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  entityStats: ReconciliationRunEntityStat[];
}

export interface ReconciliationEntityArtifacts {
  runId: string;
  tenantId: string;
  entityKey: string;
  datasetName: string;
  taskStatus: string;
  state: string;
  resultStatus?: ReconciliationResultStatus;
  metrics: ReconciliationEntityMetrics;
  deltaSummary?: Record<string, unknown>;
  diffManifest?: Record<string, unknown>;
  missingInRedshift: string[];
  extraInRedshift: string[];
  topStaleIds: Array<{
    id: string;
    clioUpdatedAt: string;
    redshiftUpdatedAt: string;
    lagMinutes: number;
  }>;
  healActions: Array<{
    entityId: string;
    action: string;
    reason: string;
    requested: boolean;
    mechanism: string;
    error?: string;
    createdAt: string;
  }>;
}

export interface TenantWarehouseTableMetric {
  tableName: string;
  category: 'dataset' | 'clio_report';
  mode: 'regular' | 'monthly-report' | 'snapshot-report';
  reportKind?: string;
  recordCount: number;
  maxIngestedAt: string | null;
  ingestTimestampColumn: 'dw_ingested_at' | 'last_replicated_at' | null;
}

export interface TenantWarehouseReportMonthMetric {
  reportMonthCode: string | null;
  recordCount: number;
  maxIngestedAt: string | null;
}

export interface TenantWarehouseMonthlyReportMetric {
  tableName: string;
  reportKind: string;
  recordCount: number;
  maxIngestedAt: string | null;
  ingestTimestampColumn: 'dw_ingested_at' | 'last_replicated_at' | null;
  months: TenantWarehouseReportMonthMetric[];
}

export interface TenantWarehouseMetrics {
  tenantId: string;
  generatedAt: string;
  tables: TenantWarehouseTableMetric[];
  monthlyReports: TenantWarehouseMonthlyReportMetric[];
}

class ReconciliationService {
  async startRun(payload: ReconciliationRunStartRequest): Promise<{ runId: string }> {
    const response = await apiClient.post<{ runId: string }>('/admin/reconciliation/runs', payload);
    return response.data;
  }

  async listRuns(filters?: {
    tenantId?: string;
    status?: ReconciliationRunStatus;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }): Promise<{ runs: ReconciliationRunListItem[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.set('dateTo', filters.dateTo);
    if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));

    const query = params.toString();
    const endpoint = query ? `/admin/reconciliation/runs?${query}` : '/admin/reconciliation/runs';

    const response = await apiClient.get<{ runs: ReconciliationRunListItem[]; total: number }>(endpoint);
    return response.data;
  }

  async getRun(runId: string): Promise<ReconciliationRunDetail> {
    const response = await apiClient.get<ReconciliationRunDetail>(`/admin/reconciliation/runs/${runId}`);
    return response.data;
  }

  async getEntityArtifacts(runId: string, entityKey: string, tenantId?: string): Promise<ReconciliationEntityArtifacts> {
    const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    const response = await apiClient.get<ReconciliationEntityArtifacts>(`/admin/reconciliation/runs/${runId}/entities/${encodeURIComponent(entityKey)}${query}`);
    return response.data;
  }

  async cancelRun(runId: string): Promise<{ runId: string; status: string }> {
    const response = await apiClient.post<{ runId: string; status: string }>(`/admin/reconciliation/runs/${runId}/cancel`, {});
    return response.data;
  }

  async resumeRun(runId: string): Promise<{ runId: string; status: string }> {
    const response = await apiClient.post<{ runId: string; status: string }>(`/admin/reconciliation/runs/${runId}/resume`, {});
    return response.data;
  }

  async retryEntity(runId: string, entityKey: string): Promise<{ runId: string; entityKey: string; taskStatus: string; state: string }> {
    const response = await apiClient.post<{ runId: string; entityKey: string; taskStatus: string; state: string }>(
      `/admin/reconciliation/runs/${runId}/retry-entity/${encodeURIComponent(entityKey)}`,
      {}
    );
    return response.data;
  }

  async getTenantWarehouseMetrics(tenantId: string): Promise<TenantWarehouseMetrics> {
    const response = await apiClient.get<TenantWarehouseMetrics>(
      `/admin/reconciliation/tenant/${encodeURIComponent(tenantId)}/warehouse-metrics`
    );
    return response.data;
  }
}

export const reconciliationService = new ReconciliationService();
